import {
  awsPublishEmbeds,
  awsRestoreEmbedBackup,
  awsVerifyEmbeds,
} from "../data/api/embedApi";
import {
  EMBED_TARGET,
  buildBotConfig,
  embedFileName,
  generateEmbedScript,
} from "./embedScripts";

const normalize = value => (value || "").replace(/\r\n/g, "\n").trim();

const extractLiveVersion = content => {
  const match = content.match(/"botpressVersion":\s*"([^"]+)"/);
  return match ? match[1] : null;
};

const compareToLive = (bot, live) => {
  const expectedVersion = buildBotConfig(bot).botpressVersion;

  if (!live) {
    return {
      key: bot.key,
      status: "error",
      httpStatus: null,
      expectedVersion,
      liveVersion: null,
    };
  }

  if (live.httpStatus !== 200 || live.content == null) {
    return {
      key: bot.key,
      status: "missing",
      httpStatus: live.httpStatus ?? null,
      expectedVersion,
      liveVersion: null,
    };
  }

  const upToDate = normalize(live.content) === normalize(generateEmbedScript(bot));

  return {
    key: bot.key,
    status: upToDate ? "up-to-date" : "needs-update",
    httpStatus: 200,
    expectedVersion,
    liveVersion: extractLiveVersion(live.content),
  };
};

export const verifyLiveScripts = async (bots, onProgress) => {
  const results = {};

  try {
    const response = await awsVerifyEmbeds({
      domain: EMBED_TARGET.domain,
      files: bots.map(embedFileName),
    });

    const byFile = {};
    (response?.results || []).forEach(result => {
      byFile[result.fileName] = result;
    });

    bots.forEach(bot => {
      const result = compareToLive(bot, byFile[embedFileName(bot)]);
      results[bot.key] = result;
      if (onProgress) {
        onProgress(bot.key, result);
      }
    });
  } catch (error) {
    bots.forEach(bot => {
      const result = {
        key: bot.key,
        status: "error",
        httpStatus: null,
        expectedVersion: buildBotConfig(bot).botpressVersion,
        liveVersion: null,
        error: error.message,
      };
      results[bot.key] = result;
      if (onProgress) {
        onProgress(bot.key, result);
      }
    });
  }

  return results;
};

export const publishScripts = async bots => {
  const files = bots.map(bot => ({
    key: bot.key,
    fileName: embedFileName(bot),
    content: generateEmbedScript(bot),
  }));

  const response = await awsPublishEmbeds({
    bucket: EMBED_TARGET.bucket,
    distributionId: EMBED_TARGET.distributionId,
    backupPrefix: EMBED_TARGET.backupPrefix,
    contentType: "application/javascript",
    cacheControl: EMBED_TARGET.cacheControl,
    files,
  });

  return {
    backupFolder: response?.backupFolder || null,
    published: response?.published || files.map(file => ({ key: file.key, fileName: file.fileName })),
    invalidationId: response?.invalidationId || null,
  };
};

export const publishSingleScript = bot => publishScripts([bot]);

export const restoreBackup = async (backupFolder, bots) => {
  const response = await awsRestoreEmbedBackup({
    bucket: EMBED_TARGET.bucket,
    distributionId: EMBED_TARGET.distributionId,
    backupFolder,
    keys: bots.map(bot => embedFileName(bot)),
    contentType: "application/javascript",
    cacheControl: EMBED_TARGET.cacheControl,
  });

  return { backupFolder, invalidationId: response?.invalidationId || null };
};
